import * as React from 'react';
import API, { IPagedResultSet } from "../../common/API";
import { useAuth } from "react-oidc-context";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CourseStatus } from '../../common/TrainingTypes';
import { LinkContainer } from 'react-router-bootstrap';
import { usePagination } from '../../hooks/usePagination';
import { useTranslation } from 'react-i18next';

interface TrainingUserDTO {
    id: number;
    name: string;
    completion: number;
    total: number;
    lastCourseId: number | null;
    lastCourse: string | null;
    lastCourseGrade: number | null;
    lastCompletion: Date | null;
}

interface UserTrainingResultDTO {
    courseId: number;
    course: string;
    category: string;
    status: CourseStatus;
    grade: number | null;
    startedOn: Date | null;
    completedOn: Date | null;
}

interface CategoryGroup {
    name: string;
    passed: number;
    courses: UserTrainingResultDTO[]
}

function CategoryComponent(props: CategoryGroup) {
    const [expanded, setExpanded] = React.useState<boolean>(false);
    const { t } = useTranslation();

    return (
        <>
            <thead style={{ borderTop: 0 }}>
                <tr className="table-light"><td colSpan={4}>
                    <button type="button" className="btn btn-sm btn-primary me-2" onClick={function () { setExpanded(!expanded); } }>
                        <FontAwesomeIcon icon={expanded?"minus":"plus"}></FontAwesomeIcon>
                    </button>
                    <strong>{props.name}</strong> ({props.passed}/{props.courses.length})
                </td></tr>
            </thead>
            <tbody style={{ borderTop: 0 }}>
                {expanded && props.courses?.map((course) => {
                    return (
                        <tr>
                            <td className="ps-5"><LinkContainer to={"/training/course/" + course.courseId}><a>{course.course}</a></LinkContainer></td>
                            <td>
                                {course.status === CourseStatus.Pass ? <div className="badge my-2 bg-success">{course.grade}%</div> :
                                 course.status === CourseStatus.Expired ? <div className="badge my-2 bg-warning">{t('training:expired')}</div> :
                                 course.status === CourseStatus.InProgress ? <div className="badge my-2 bg-info">{t('training:inProgress')}</div> :
                                <div className="badge my-2 bg-primary">{t('training:new')}</div>}
                            </td>
                            <td>{course.startedOn != null ? new Date(course.startedOn + "Z").toLocaleString() : ""}</td>
                            <td>{course.completedOn != null ? new Date(course.completedOn + "Z").toLocaleString() : ""}</td>
                        </tr>
                    )
                })}
            </tbody>
        </>
    )
}

export function UserTrainingComponent() {
    const auth = useAuth();
    const { t } = useTranslation();

    const [search, setSearch] = React.useState<string>();
    const [users, pagination, setCustomParams] = usePagination<TrainingUserDTO>({perPage: 15, route: '/api/v1/training/users'})

    const [selectedUser, setSelectedUser] = React.useState<TrainingUserDTO | null>(null);
    const [selectedUserId, setSelectedUserId] = React.useState<number>();
    const [categories, setCategories] = React.useState<CategoryGroup[] | null>();

    const searchClick = (ev: React.FormEvent) => {
        if (search !== undefined)
            setCustomParams('filter=Name.Contains("' + search + '")');
        else
            setCustomParams(undefined);

        ev.preventDefault();

        return false;
    }

    const selectUser = (e: TrainingUserDTO) => {
        setSelectedUser(e);
        setSelectedUserId(e.id);
    }

    React.useEffect(() => {
        const getUserResults = async () => {
            const response: IPagedResultSet<UserTrainingResultDTO> | null = await API.get("/api/v1/training/users/" + selectedUserId, auth);
            if (response === null)
                return;

            const categories: CategoryGroup[] = []

            response.results.forEach((course) => {
                const existing = categories.find((e) => e.name === course.category)
                if (existing === undefined) {
                    categories.push({
                        name: course.category,
                        passed: course.status === CourseStatus.Pass ? 1 : 0,
                        courses: [course]
                    });
                } else {
                    if (course.status === CourseStatus.Pass)
                        existing.passed++;
                    existing.courses.push(course);
                }
            })
            setCategories(categories);
        }

        if (selectedUserId !== null)
            getUserResults();

    }, [auth, selectedUserId])

    return (
        <>
            <h1 className="mb-3">
                <FontAwesomeIcon icon="graduation-cap" className="me-2" />
                <span>{t('training:userTraining')}</span>
            </h1>
            <div className="row">
                <div className="col-md-5">
                    <div className="card">
                        <div className="card-header d-flex align-items-center">
                            <h5 className="card-title flex-grow-1">{t('common:users')}</h5>
                            <form onSubmit={searchClick} className="input-group w-50">
                                <input type="text" className="form-control" style={{ minWidth: '150px' }} placeholder="Search" onChange={(ev) => setSearch(ev.target.value)} />
                                <button className="btn btn-primary" type="submit"><FontAwesomeIcon icon="search" /></button>
                            </form>
                        </div>
                        <div className="card-body p-0">
                            <table className="table table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: '30%' }}>{t('common:user')}</th>
                                        <th style={{ width: '10%' }}>{t('training:completed')}</th>
                                        <th style={{ width: '60%' }}>{t('training:lastCourse')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((e) => {
                                        return (
                                            <tr key={e.id} className={"cursor-pointer " + (selectedUserId === e.id ? "table-active" : "")} onClick={(ev) => selectUser(e)}>
                                                <td>{e.name}</td>
                                                <td>{e.completion} / {e.total} ({Math.round((e.completion / e.total) * 100)}%)</td>
                                                <td>{e.lastCompletion != null ? <span> {e.lastCourse} &bull; {e.lastCourseGrade}% &bull; {new Date(e.lastCompletion + "Z").toLocaleString()}</span>
                                                    : <></>}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                    {users.length === 0 ? <tr><td colSpan={3}>{t('training:noUsersFound')}</td></tr> : <></>}
                                </tbody>
                            </table>
                        </div>
                        <div className="card-footer">{pagination()}</div>
                    </div>
                </div>
                <div className="col-md-7">
                    <div className="card">
                        {selectedUser !== null ?
                            <div className="card-header">
                                <h5 className="card-title">{selectedUser.name}</h5>
                                <h6 className="card-subtitle text-muted">
                                    {selectedUser.completion} / {selectedUser.total} ({Math.round((selectedUser.completion / selectedUser.total) * 100)}%)
                                    {t('training:coursesCompleted')}
                                </h6>

                            </div> : <></>}
                        <div className="card-body p-0">
                            {selectedUser == null ?
                                <div className="alert alert-info alert-outline alert-dismissible m-3" role="alert">
                                    <div className="alert-icon"><FontAwesomeIcon icon="info" /></div>
                                    <div className="alert-message">
                                        <strong>{t('training:note')}  </strong>
                                        {t('training:selectAUser')}
                                    </div>
                                </div> :
                                <table className="table table-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '50%' }}>{t('training:course')}</th>
                                            <th style={{ width: '10%' }}>{t('training:status')}</th>
                                            <th style={{ width: '20%' }}>{t('training:startedOn')}</th>
                                            <th style={{ width: '20%' }}>{t('training:completedOn')}</th>
                                        </tr>
                                    </thead>
                                    {categories?.map((e) => {
                                        return (<CategoryComponent name={e.name} courses={e.courses} passed={e.passed} key={e.name} />
                                        )
                                    })}
                                </table>}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}